import { ACTION_SCHEMA_VERSION } from '@/common/actionProtocol/ActionProtocol';
const DEFAULT_RETRY_POLICY = {
    maxAttempts: 3,
    baseDelayMs: 250,
    maxDelayMs: 2000,
};
/** Keeps a settled actionId from producing a second transport effect. */
const MAX_SETTLED_OUTCOMES = 32;
const STALE_CODES = new Set([
    'STALE_PROMPT',
    'STALE_REVISION',
    'ENGINE_VERSION_MISMATCH',
]);
const ACTION_ERROR_CODES = new Set([
    'INVALID_SCHEMA',
    'UNAUTHORIZED',
    'ENGINE_VERSION_MISMATCH',
    'STALE_PROMPT',
    'STALE_REVISION',
    'INVALID_ACTION',
    'IDEMPOTENCY_CONFLICT',
    'RATE_LIMITED',
    'PERSISTENCE_FAILURE',
]);
/**
 * Client-side v2 submit owner.
 *
 * Responsibilities:
 * - preserve one actionId across clicks and retries (per-actionId single flight);
 * - retry transient network/5xx failures with bounded exponential backoff;
 * - perform exactly one full GET player resync for stale 409 responses;
 * - never let a stale success response overwrite a newer confirmed view;
 * - leave validation/auth errors terminal with no retry.
 *
 * It does not own confirmed view state: GameShellService remains the only view
 * store, and the service only publishes server-authorized responses to it.
 */
export class GameShellSubmitService {
    constructor(options) {
        this.pending = new Map();
        this.inFlight = new Map();
        this.settled = new Map();
        this.lastServerViewRevision = undefined;
        this.resyncInFlight = undefined;
        this.gameId = options.gameId;
        this.transport = options.transport;
        this.shell = options.shell;
        const requested = options.policy ?? {};
        const maxAttempts = requested.maxAttempts ?? DEFAULT_RETRY_POLICY.maxAttempts;
        const baseDelayMs = requested.baseDelayMs ?? DEFAULT_RETRY_POLICY.baseDelayMs;
        const maxDelayMs = requested.maxDelayMs ?? DEFAULT_RETRY_POLICY.maxDelayMs;
        this.policy = {
            maxAttempts: Number.isSafeInteger(maxAttempts) && maxAttempts > 0 ? maxAttempts : DEFAULT_RETRY_POLICY.maxAttempts,
            baseDelayMs: Number.isFinite(baseDelayMs) && baseDelayMs >= 0 ? baseDelayMs : DEFAULT_RETRY_POLICY.baseDelayMs,
            maxDelayMs: Number.isFinite(maxDelayMs) && maxDelayMs >= 0 ? maxDelayMs : DEFAULT_RETRY_POLICY.maxDelayMs,
        };
        this.sleep = options.sleep ?? defaultSleep;
    }
    getPendingIntents() {
        return Array.from(this.pending.values(), (pending) => ({ ...pending.intent }));
    }
    getPendingActionIds() {
        return Array.from(this.pending.keys());
    }
    hasPendingAction(actionId) {
        return this.pending.has(actionId);
    }
    /**
     * Submits an intent. A repeated call with the same actionId joins the
     * in-flight request, reuses a stored pending intent after a transient
     * failure, or returns the settled outcome without a second transport effect.
     */
    submit(intent) {
        const normalized = normalizeIntent(intent);
        const key = intentKey(normalized);
        const actionId = normalized.actionId;
        const running = this.inFlight.get(actionId);
        if (running !== undefined) {
            return running;
        }
        const settled = this.settled.get(actionId);
        if (settled !== undefined) {
            if (settled.key === key) {
                return Promise.resolve(settled.outcome);
            }
            return Promise.resolve(rejectedOutcome(actionId, 0, 'IDEMPOTENCY_CONFLICT', false, false));
        }
        let pending = this.pending.get(actionId);
        if (pending !== undefined) {
            if (pending.key !== key) {
                return Promise.resolve(rejectedOutcome(actionId, 0, 'IDEMPOTENCY_CONFLICT', false, false));
            }
        }
        else {
            pending = {
                intent: normalized,
                key,
                request: this.requestFor(normalized),
                attempts: 0,
            };
            this.pending.set(actionId, pending);
        }
        // Re-add after stop()/clearPending(); addPendingIntent is idempotent by actionId.
        this.shell.addPendingIntent({ actionId, baseRevision: pending.intent.baseRevision });
        const promise = this.run(pending);
        this.inFlight.set(actionId, promise);
        return promise;
    }
    /** Resubmits the stored intent with its original actionId (no new key). */
    retry(actionId) {
        const running = this.inFlight.get(actionId);
        if (running !== undefined) {
            return running;
        }
        const pending = this.pending.get(actionId);
        if (pending !== undefined) {
            return this.submit(pending.intent);
        }
        const settled = this.settled.get(actionId);
        if (settled !== undefined) {
            return Promise.resolve(settled.outcome);
        }
        return Promise.reject(new Error('No pending submit intent for actionId ' + actionId));
    }
    /** Clears pending intents, e.g. after a stale conflict or on logout. */
    clearPending() {
        this.pending.clear();
        this.shell.clearPendingIntents();
    }
    /** Drops local state. Does not cancel an already-sent transport request. */
    reset() {
        this.clearPending();
        this.settled.clear();
        this.lastServerViewRevision = undefined;
    }
    requestFor(intent) {
        return {
            v: ACTION_SCHEMA_VERSION,
            gameId: this.gameId,
            actionId: intent.actionId,
            baseRevision: intent.baseRevision,
            promptToken: intent.promptToken,
            engineVersion: intent.engineVersion,
            input: intent.input,
        };
    }
    async run(pending) {
        const actionId = pending.intent.actionId;
        try {
            for (let attempt = 1; attempt <= this.policy.maxAttempts; attempt++) {
                pending.attempts = attempt;
                // A view confirmed after this send makes its response stale.
                const shellRevisionAtSend = this.shell.getViewRevision();
                let response;
                try {
                    response = await this.transport.post(pending.request);
                }
                catch (error) {
                    if (attempt < this.policy.maxAttempts) {
                        await this.sleep(this.delayFor(attempt));
                        continue;
                    }
                    return this.transportFailure(pending, attempt, error);
                }
                const result = await this.handleResponse(pending, response, attempt, shellRevisionAtSend);
                if (result === 'retry') {
                    if (attempt < this.policy.maxAttempts) {
                        await this.sleep(this.delayFor(attempt));
                        continue;
                    }
                    return this.transportFailure(pending, attempt);
                }
                return result;
            }
            return this.transportFailure(pending, pending.attempts);
        }
        finally {
            this.inFlight.delete(actionId);
        }
    }
    async handleResponse(pending, response, attempt, shellRevisionAtSend) {
        const actionId = pending.intent.actionId;
        if (response.statusCode >= 200 && response.statusCode < 300 && isSuccessBody(response.body)) {
            if (response.body.actionId !== actionId) {
                return this.protocolFailure(pending, attempt);
            }
            const viewApplied = this.applyConfirmedView(response.body, shellRevisionAtSend);
            const outcome = {
                status: response.body.status,
                actionId,
                attempts: attempt,
                viewApplied,
                revision: safeRevision(response.body.revision, response.body.viewRevision),
                appliedRevision: safeRevision(response.body.appliedRevision, response.body.viewRevision),
                viewRevision: response.body.viewRevision,
                view: response.body.view,
                promptToken: response.body.promptToken,
            };
            return this.settle(pending, outcome);
        }
        const error = isRejectedBody(response.body) ? response.body : undefined;
        if (response.statusCode === 409 && error !== undefined && STALE_CODES.has(error.code)) {
            return await this.handleStale(pending, error, attempt);
        }
        if (response.statusCode >= 500 && (error === undefined || error.retryable !== false)) {
            return 'retry';
        }
        const code = error !== undefined && isKnownErrorCode(error.code) ? error.code : codeForStatus(response.statusCode);
        const retryable = error !== undefined && error.retryable === true;
        return this.settle(pending, rejectedOutcome(actionId, attempt, code, retryable, false));
    }
    async handleStale(pending, error, attempt) {
        // The conflict invalidates every locally pending baseRevision/promptToken.
        this.clearPending();
        const resynced = await this.resyncOnce();
        if (!resynced) {
            return {
                status: 'failed',
                actionId: pending.intent.actionId,
                attempts: attempt,
                reason: 'resync',
                retryable: false,
                code: error.code,
            };
        }
        const outcome = {
            status: 'rejected',
            actionId: pending.intent.actionId,
            attempts: attempt,
            code: error.code,
            retryable: false,
            resynced: true,
        };
        this.remember(pending.key, outcome);
        return outcome;
    }
    resyncOnce() {
        if (this.resyncInFlight !== undefined) {
            return this.resyncInFlight;
        }
        const revisionAtStart = this.shell.getViewRevision();
        const playerId = this.shell.getConfirmedView()?.id;
        const resync = this.performResync(revisionAtStart, playerId);
        this.resyncInFlight = resync;
        return resync;
    }
    async performResync(revisionAtStart, playerId) {
        try {
            const view = await this.transport.fetchPlayerView(playerId);
            // A success that landed while the GET was in flight is newer; keep it.
            if (this.shell.getViewRevision() === revisionAtStart) {
                this.lastServerViewRevision = undefined;
                this.shell.setConfirmedView(view);
            }
            return true;
        }
        catch {
            return false;
        }
        finally {
            this.resyncInFlight = undefined;
        }
    }
    applyConfirmedView(body, shellRevisionAtSend) {
        if (this.shell.getViewRevision() !== shellRevisionAtSend) {
            return false;
        }
        if (this.lastServerViewRevision !== undefined && body.viewRevision < this.lastServerViewRevision) {
            return false;
        }
        this.lastServerViewRevision = body.viewRevision;
        this.shell.setConfirmedView(body.view);
        return true;
    }
    settle(pending, outcome) {
        this.pending.delete(pending.intent.actionId);
        this.shell.removePendingIntent(pending.intent.actionId);
        this.remember(pending.key, outcome);
        return outcome;
    }
    remember(key, outcome) {
        this.settled.delete(outcome.actionId);
        this.settled.set(outcome.actionId, { key, outcome });
        while (this.settled.size > MAX_SETTLED_OUTCOMES) {
            const oldest = this.settled.keys().next().value;
            if (oldest === undefined) {
                break;
            }
            this.settled.delete(oldest);
        }
    }
    transportFailure(pending, attempts, error) {
        const outcome = {
            status: 'failed',
            actionId: pending.intent.actionId,
            attempts,
            reason: 'transport',
            retryable: true,
        };
        if (error !== undefined) {
            outcome.error = error;
        }
        return outcome;
    }
    protocolFailure(pending, attempts) {
        // Keep the pending intent: retrying the same actionId is safe and may
        // recover the authoritative response after a malformed/truncated body.
        return {
            status: 'failed',
            actionId: pending.intent.actionId,
            attempts,
            reason: 'protocol',
            retryable: false,
        };
    }
    delayFor(attempt) {
        const exponential = this.policy.baseDelayMs * Math.pow(2, attempt - 1);
        return Math.min(exponential, this.policy.maxDelayMs);
    }
}
function defaultSleep(delayMs) {
    return new Promise((resolve) => {
        setTimeout(resolve, delayMs);
    });
}
function normalizeIntent(intent) {
    if (typeof intent.actionId !== 'string' || intent.actionId.length === 0) {
        throw new Error('GameShellSubmitIntent requires a non-empty actionId');
    }
    if (!Number.isSafeInteger(intent.baseRevision) || intent.baseRevision < 0) {
        throw new Error('GameShellSubmitIntent requires a non-negative integer baseRevision');
    }
    if (typeof intent.promptToken !== 'string' || intent.promptToken.length === 0) {
        throw new Error('GameShellSubmitIntent requires a server-issued promptToken');
    }
    if (typeof intent.engineVersion !== 'string' || intent.engineVersion.length === 0) {
        throw new Error('GameShellSubmitIntent requires an engineVersion');
    }
    if (typeof intent.input !== 'object' || intent.input === null || Array.isArray(intent.input)) {
        throw new Error('GameShellSubmitIntent requires an input object');
    }
    return {
        actionId: intent.actionId,
        baseRevision: intent.baseRevision,
        promptToken: intent.promptToken,
        engineVersion: intent.engineVersion,
        input: cloneInput(intent.input),
    };
}
function cloneInput(input) {
    return JSON.parse(JSON.stringify(input));
}
function intentKey(intent) {
    return JSON.stringify(intent);
}
function rejectedOutcome(actionId, attempts, code, retryable, resynced) {
    return { status: 'rejected', actionId, attempts, code, retryable, resynced };
}
function isSuccessBody(body) {
    if (body === undefined) {
        return false;
    }
    if (body.status !== 'applied' && body.status !== 'duplicate') {
        return false;
    }
    return typeof body.actionId === 'string' &&
        typeof body.view === 'object' && body.view !== null &&
        Number.isSafeInteger(body.viewRevision) && body.viewRevision >= 0;
}
function isRejectedBody(body) {
    return body !== undefined && body.status === 'rejected' && typeof body.code === 'string';
}
function isKnownErrorCode(code) {
    return ACTION_ERROR_CODES.has(code);
}
function safeRevision(value, fallback) {
    return Number.isSafeInteger(value) && value >= 0 ? value : fallback;
}
function codeForStatus(statusCode) {
    if (statusCode === 400 || statusCode === 413) {
        return 'INVALID_SCHEMA';
    }
    if (statusCode === 401 || statusCode === 403 || statusCode === 404) {
        return 'UNAUTHORIZED';
    }
    if (statusCode === 409) {
        return 'IDEMPOTENCY_CONFLICT';
    }
    if (statusCode === 422) {
        return 'INVALID_ACTION';
    }
    if (statusCode === 429) {
        return 'RATE_LIMITED';
    }
    return 'PERSISTENCE_FAILURE';
}
